// Translates a raw Enterprise template row — the shape returned by the list endpoint and
// already consumed by helper/utils.js's mapTemplate and Templates/DetailModal.js — into this
// form's state shape (state.js's initialFormState()). This is the inverse of payload.js:
// payload.js builds the wire request FROM state; this file builds state FROM a row already on
// the wire.
//
// Known limitation (real, verified 2026-09-22): `body_examples` / `header_examples` — the
// variable sample values — can be entirely ABSENT even on a template that genuinely has
// variables, because a backend change to request those fields from upstream was written but
// not deployed as of this task. buildVariableRows degrades to an empty sample value per token
// in that case rather than guessing or crashing. Every other judgment call under real
// uncertainty (subcategory inference, button reconstruction, media re-upload) is called out at
// the point it's made below.
//
// Plain JS, no JSX, no new dependency — same category of file as state.js/payload.js/validate.js.

import {
  BUTTON_TYPE,
  URL_TYPE,
  OTP_TYPE,
  HEADER_TYPE,
  MEDIA_TYPE,
  CATEGORY,
  SUBCATEGORY,
  CUSTOM_MESSAGE_SUBCATEGORY,
  CAROUSEL_MEDIA_TYPE,
  FLOW_ACTION,
  MARKETING_OPT_OUT_BUTTON_TEXT,
  MARKETING_OPT_OUT_FOOTER,
  REQUEST_CONTACT_INFO_BUTTON_TEXT,
  createAppSetup,
  createButton,
  createCarouselCard,
  createId,
  extractTokens,
  field,
  initialFormState,
} from './state';
import { toFormBodyNumbering } from '../../../helper/templateVariables';

// Zips {{n}} tokens (document order) 1:1 against a raw row's example array by position — one
// shared helper for both body.variable and header.headerText.variable (the header case only
// ever has 0 or 1 token in practice, so this naturally produces "one variable row" there without
// needing a separate index-0-only function).
//
// Judgment call: if the token count doesn't match the examples array length — including the
// examples array being entirely absent, per the module-level gap noted above — every token
// falls back to an empty sample value rather than guessing an alignment that could be wrong.
function buildVariableRows(text, examples) {
  const tokens = extractTokens(text);
  const hasAlignedExamples = Array.isArray(examples) && examples.length === tokens.length;
  return tokens.map((name, index) => ({
    name,
    value: hasAlignedExamples ? (examples[index] ?? '') : '',
    error: '',
  }));
}

// Reconstructs one state.buttons[] entry from a raw wire-shaped button object. The wire shape
// (type/text/url/example/phone_number/otp_type/...) is confirmed by reading how the rest of this
// app already renders it, not guessed: helper/utils.js's mapTemplate spreads the raw template
// row unchanged into containerMeta, and components/TemplatePreview/WhatsAppPreview.js feeds
// `containerMeta.buttons` straight into ButtonPanel.js, which reads btn.type/text/url/example/
// phone_number/otp_type directly off it. That means the field lives at the top level as
// `template.buttons` — not nested under a `components` wrapper the way carousel cards are (see
// buildCarouselCardFromRaw below, which is the one place that wrapper really exists).
//
// Always built through createButton() first, per state.js's own factory rule — never a
// hand-rolled object.
function reconstructButton(wireBtn) {
  if (!wireBtn || typeof wireBtn !== 'object') return null;
  const { type } = wireBtn;
  // Unknown/legacy wire type — e.g. "CPR" (Call Permission Request), a concept
  // components/TemplatePreview still special-cases via containerMeta.isCPR but that has no
  // counterpart at all in this form's BUTTON_TYPE. Skip rather than seed a button the rest of
  // the form has no UI to render or validate.
  if (!Object.values(BUTTON_TYPE).includes(type)) return null;

  const btn = createButton(type);
  btn.text = field(wireBtn.text || '');

  if (type === BUTTON_TYPE.URL) {
    // DYNAMIC_LINK_TRACK: Enterprise fills url/example itself from the account's link-tracking
    // domain and never returns anything we sent (URLButton.java:166-199) — the read url (e.g.
    // "https://m.gs.im/e/{{1}}") IS that domain, not something to send back. Bug fix: this used
    // to fall through to the STATIC branch below with the literal "{{1}}" left in buttonValue,
    // breaking edit/clone of every link-tracked template.
    const isLinkTrack = String(wireBtn.urlType || '').toUpperCase() === URL_TYPE.DYNAMIC_LINK_TRACK;
    // Wire key is `appDeepLink` (URLButton.java has no @SerializedName, so Gson uses the Java
    // field name verbatim) — `app_deep_link` kept as a fallback for safety, not because it's a
    // real alternate wire shape.
    const deepLink = wireBtn.appDeepLink ?? wireBtn.app_deep_link ?? {};
    if (isLinkTrack) {
      btn.urlType = field(URL_TYPE.DYNAMIC_LINK_TRACK);
      btn.buttonValue = field('');
      btn.suffix = field('');
    } else {
      const isDynamic = wireBtn.urlType === URL_TYPE.DYNAMIC;
      const rawUrl = wireBtn.url || '';
      // WRITE side (payload.js): we send the base url WITHOUT `{{1}}` — Enterprise requires
      // example[0] to start with the url we send (URLButton.java:159-161) and appends `{{1}}` itself
      // when building the Meta payload (MessageTemplateService.java:2423). payload.js must never add
      // `{{1}}` itself — that breaks every dynamic-url save.
      //
      // READ side (this function): Meta's own template JSON puts the variable placeholder literally
      // inside `url`, always as the last thing in it — confirmed against Enterprise's own source,
      // both directly (TemplateUtils.getWhatsAppButton flags a button DYNAMIC by
      // `url.contains("{{1}}")`) and by a sibling method that recovers the base by slicing off
      // exactly the last 5 characters — the length of the literal "{{1}}" — rather than searching
      // for it, which only works if it's always at the end. So a real dynamic button's `url` looks
      // like "https://shop.com/orders/{{1}}", not a bare base. Cut at the marker to recover that
      // base; if the marker is missing or something trails it (should never happen, per the above),
      // leave `base` as the full raw url instead of silently cutting text off the end — an
      // odd-looking value the user can see and fix, not a quiet data loss.
      const markerIndex = rawUrl.indexOf('{{1}}');
      const markerIsTrailing = markerIndex >= 0 && markerIndex + '{{1}}'.length === rawUrl.length;
      const base = (isDynamic && markerIsTrailing) ? rawUrl.slice(0, markerIndex) : rawUrl;
      const example0 = Array.isArray(wireBtn.example) ? wireBtn.example[0] : undefined;
      // example[0] is the FULLY RESOLVED url (base + the variable's sample value substituted in) —
      // Meta's own convention, not the sample value alone. Recover the sample value (suffix) by
      // stripping the base back off the front. Only trust that split when example0 genuinely starts
      // with base as a literal prefix; otherwise suffix stays empty and buttonValue (below) stays
      // the raw, uncut url — visible and fixable rather than a guessed split silently corrupting a
      // previously-working button on the next save.
      let suffix = '';
      if (isDynamic && typeof example0 === 'string' && example0.startsWith(base)) {
        suffix = example0.slice(base.length);
      }
      btn.urlType = field(isDynamic ? URL_TYPE.DYNAMIC : URL_TYPE.STATIC);
      btn.buttonValue = field(base);
      btn.suffix = field(suffix);
    }
    btn.hasDeepLink = Boolean(deepLink.android_deep_link || deepLink.android_fallback_playstore_url);
    btn.deepLink = field(deepLink.android_deep_link || '');
    btn.fallbackLink = field(deepLink.android_fallback_playstore_url || '');
  } else if (type === BUTTON_TYPE.PHONE_NUMBER) {
    btn.buttonValue = field(wireBtn.phone_number || '');
  } else if (type === BUTTON_TYPE.COPY_CODE) {
    // example is a plain STRING for COPY_CODE (payload.js) — every other type's example is an
    // array. Guarded so a malformed array here doesn't get assigned as-is.
    btn.buttonValue = field(typeof wireBtn.example === 'string' ? wireBtn.example : '');
  } else if (type === BUTTON_TYPE.FLOW) {
    btn.buttonValue = field(wireBtn.navigate_screen || '');
    btn.flowId = field(wireBtn.flow_id !== undefined && wireBtn.flow_id !== null ? String(wireBtn.flow_id) : '');
    // The Select only offers FLOW_ACTION's two values (navigate/data_exchange) — default to
    // navigate when the row's value is absent or anything else, so the Select never renders blank.
    // Bug fix: Enterprise returns this UPPERCASE ("NAVIGATE"/"DATA_EXCHANGE") while FLOW_ACTION's
    // own values are lowercase (state.js — verified against flow-button.dto.ts's FlowAction
    // enum). The old case-sensitive `.includes(wireBtn.flow_action)` check never matched either
    // value, so every real row fell through to the NAVIGATE default — invisible only because
    // NAVIGATE happens to be both the fallback AND the common case; a DATA_EXCHANGE flow would
    // have silently come back as NAVIGATE. Lowercase the wire value before checking membership.
    const knownFlowActions = Object.values(FLOW_ACTION);
    const wireFlowAction = typeof wireBtn.flow_action === 'string' ? wireBtn.flow_action.toLowerCase() : '';
    btn.flowAction = field(knownFlowActions.includes(wireFlowAction) ? wireFlowAction : FLOW_ACTION.NAVIGATE);
  } else if (type === BUTTON_TYPE.VOICE_CALL) {
    // ttl_minutes is minutes; state's activeFor is days (payload.js multiplies by 24*60 on write).
    // Rounded to a whole day, minimum 1, so a non-exact multiple of a day (e.g. 2000 minutes)
    // never displays a fractional value like 1.3888... in the days field.
    const ttlMinutes = Number(wireBtn.ttl_minutes) || 0;
    btn.activeFor = field(ttlMinutes ? String(Math.max(1, Math.round(ttlMinutes / (24 * 60)))) : '');
  } else if (type === BUTTON_TYPE.OTP) {
    btn.autofillText = field(wireBtn.autofill_text || '');
    btn.zeroTapTermsAccepted = Boolean(wireBtn.zero_tap_terms_accepted);
    // wireBtn.supported_apps -> state.appsSetup is a known gap (see prefillFormState) — not
    // reconstructed here, on purpose.
  } else if (type === BUTTON_TYPE.REQUEST_CONTACT_INFO) {
    // The console fixes this button's title to a constant and disables editing it (Buttons.js,
    // T5) — show the same label even if Enterprise's own stored text ever comes back blank
    // (RequestContactInfoButtonDto.text is optional on the wire).
    btn.text = field(wireBtn.text || REQUEST_CONTACT_INFO_BUTTON_TEXT);
  }
  // QUICK_REPLY / ORDER_DETAILS / PASSKEY need no extra fields beyond the text already set above
  // (PASSKEY's is overwritten to the fixed 'Verify' by payload.js on every submit regardless, but
  // validate.js still reads btn.text.value unconditionally, so it must exist as a real
  // {value,error} field either way).

  return btn;
}

// Infers category.selectedSubcategory.value for an AUTHENTICATION template from the button shape
// already on the row, or returns null when nothing at all is derivable (caller then seeds a
// default). Returns the raw wire button the inference actually came from too (`sourceButton`),
// when it came from a button at all — the caller uses it to seed the auth button's text, and
// detectDroppedFeatures uses it to tell "this button's content was captured" apart from "this
// button was genuinely dropped".
//
// Priority (verified against ce-api's TemplateUtils.java/MessageTemplateService.java by hand):
//   1. a PASSKEY-typed button.
//   2. an OTP-typed button, keyed off its own otp_type. SUBCATEGORY.COPY_CODE/AUTOFILL/ZERO_TAP
//      share their literal wire values with OTP_TYPE.COPY_CODE/ONE_TAP/ZERO_TAP by design
//      (state.js), so otp_type maps straight across with no translation table needed. Kept for
//      forward-compatibility even though TemplateUtils.getWhatsAppButton — the function that
//      actually builds the `buttons` this form reads — has no OTP case today, so this branch
//      never fires against a real row yet (see rung 3, which is what fires instead).
//   3. a URL-typed button whose url encodes otp_type= — Meta can return an auth button this way,
//      and Enterprise's own MessageTemplateService reads that same convention
//      (otp_type=COPY_CODE / ONE_TAP / ZERO_TAP / NO_BUTTONS in the query string) elsewhere.
//      Decoded exactly like the console's getAuthSubcategory
//      (gs-ui-templates/.../prefillCreateTemplateForm.ts:386-410): a plain substring match
//      against the url, not a query-param parse — COPY_CODE/ZERO_TAP win if present anywhere in
//      the url, ONE_TAP (and anything else) falls through to AUTOFILL.
//   4. template.sub_category, only when it exactly matches one of our own SUBCATEGORY literals —
//      real values on a row are unverified, so a non-matching value is ignored rather than
//      guessed at.
//   5. Truly nothing derivable — caller falls back to Copy Code.
function inferAuthSubcategory(source, rawButtons) {
  const passkeyBtn = rawButtons.find((btn) => btn?.type === BUTTON_TYPE.PASSKEY);
  if (passkeyBtn) return { subcategory: SUBCATEGORY.PASSKEY, sourceButton: passkeyBtn };

  const otpBtn = rawButtons.find((btn) => btn?.type === BUTTON_TYPE.OTP);
  if (otpBtn) {
    if (otpBtn.otp_type === OTP_TYPE.COPY_CODE) return { subcategory: SUBCATEGORY.COPY_CODE, sourceButton: otpBtn };
    if (otpBtn.otp_type === OTP_TYPE.ONE_TAP) return { subcategory: SUBCATEGORY.AUTOFILL, sourceButton: otpBtn };
    if (otpBtn.otp_type === OTP_TYPE.ZERO_TAP) return { subcategory: SUBCATEGORY.ZERO_TAP, sourceButton: otpBtn };
    // otp_type is NO_BUTTONS, or some other value we don't recognise: no SUBCATEGORY entry
    // represents a bare/no-CTA OTP template, so leave it blank rather than guess one of the
    // other three. Deliberately distinct from the "nothing derivable at all" case below — an
    // OTP button really does exist here, so the caller must NOT seed a second one.
    return { subcategory: '', sourceButton: otpBtn };
  }

  const urlOtpBtn = rawButtons.find(
    (btn) => btn?.type === BUTTON_TYPE.URL && typeof btn.url === 'string' && btn.url.includes('otp_type='),
  );
  if (urlOtpBtn) {
    let subcategory = SUBCATEGORY.AUTOFILL;
    if (urlOtpBtn.url.includes(SUBCATEGORY.COPY_CODE)) subcategory = SUBCATEGORY.COPY_CODE;
    else if (urlOtpBtn.url.includes(SUBCATEGORY.ZERO_TAP)) subcategory = SUBCATEGORY.ZERO_TAP;
    return { subcategory, sourceButton: urlOtpBtn };
  }

  // No OTP/PASSKEY/OTP-shaped-URL button found on the row at all. UNVERIFIED fallback: only trust
  // template.sub_category if it happens to exactly match one of our own SUBCATEGORY literals —
  // casing/vocabulary on a real row has not been confirmed, so a non-matching value is ignored
  // rather than guessed at.
  const rawSub = source.sub_category;
  if (rawSub && Object.values(SUBCATEGORY).includes(rawSub)) return { subcategory: rawSub, sourceButton: null };

  // Truly nothing derivable.
  return null;
}

// Parses the extra fields Enterprise packs into an OTP-shaped URL button's own `url` (see
// inferAuthSubcategory's rung 3 above for the wire shape), on top of the otp_type/text
// inferAuthSubcategory already reads off it: package_name/signature_hash (the autofill app's
// identity -> state.appsSetup), code_expiration_minutes (-> state.messageContent) and
// cta_display_name (the autofill button's own display text -> state.buttons[0].autofillText,
// console mirror: prefillCreateTemplateForm.ts:180-199's getAutoFillData/ctaDisplayName). URL's
// searchParams does the percent-decoding for us (K8a%2FAINcGX7 -> K8a/AINcGX7) — do not re-decode
// its output. codeExpirationMinutes comes back as the raw string (or null when absent), matching
// state.messageContent.expirationTime's own {value, error} string convention — payload.js's own
// Number(...) conversion happens at submit time, same as every other numeric-looking form field.
function parseOtpUrlParams(url) {
  try {
    const params = new URL(url).searchParams;
    return {
      packageName: params.get('package_name') || '',
      signatureHash: params.get('signature_hash') || '',
      codeExpirationMinutes: params.get('code_expiration_minutes'),
      ctaDisplayName: params.get('cta_display_name') || '',
    };
  } catch {
    // Malformed url — should never happen against a real row (reconstructButton would otherwise
    // treat this same button as an ordinary URL CTA), guarded rather than letting the whole
    // prefill throw over one unparseable field.
    return {
      packageName: '', signatureHash: '', codeExpirationMinutes: null, ctaDisplayName: '',
    };
  }
}

// Builds one carousels.cards[] entry from a raw carouselCards[i] row. Mirrors helper/utils.js's
// mapTemplate unpacking of that card's `.components` array (header/body/buttons, Meta-component
// shaped, lowercase `type` values) — the one verified reading path for carousel card data in
// this codebase — rather than inventing a flatter shape.
function buildCarouselCardFromRaw(cardObj) {
  const card = createCarouselCard(createId('card'));

  const comp = {};
  const components = Array.isArray(cardObj?.components) ? cardObj.components : [];
  components.forEach((component) => {
    switch (component?.type) {
      case 'header':
        comp.headerType = component?.format;
        // The card's existing media CDN URL, when Enterprise resolved one — same shape as the
        // top-level header's source.header_examples (TemplateUtils.java:302-316), just nested
        // under this card's own header component instead of the template's top level.
        comp.headerMediaSrc = component?.example?.header_handle?.[0];
        break;
      case 'body':
        comp.body = component?.text || '';
        // Nested List<List<String>> on the wire (BodyComponent.java:18-19) — one inner array of
        // sample values, populated at TemplateUtils.java:318-337. Only the first (only) inner
        // array is ever populated.
        comp.bodyExamples = component?.example?.body_text?.[0];
        break;
      case 'buttons':
        comp.buttons = component?.buttons;
        break;
      default:
        break;
    }
  });

  card.cardMedia.value = comp.headerType || CAROUSEL_MEDIA_TYPE.IMAGE;
  // comp.headerMediaSrc (above) is a real, re-fetchable CDN URL when present — the card's own
  // HEADER component's example.header_handle[0] (TemplateUtils.java:302-316), same convention as
  // the top-level header's source.header_examples[0] just nested one level deeper. Safe to
  // populate for preview now that validateCarousel keys its required-re-upload check on
  // cardMedia.mediaId alone (Form/validate.js) — a populated mediaSrc with no real re-upload
  // (mediaId still unset) still correctly fails submission, same as the top-level header case
  // below. mediaId/file stay blank either way — re-editing this card's image/video isn't possible
  // through this form yet, the user must re-attach it.
  card.cardMedia.mediaSrc = comp.headerMediaSrc || '';
  card.description = field(comp.body || '');
  card.variable = buildVariableRows(comp.body || '', comp.bodyExamples);
  const cardButtons = Array.isArray(comp.buttons) ? comp.buttons : [];
  card.buttons = cardButtons.map(reconstructButton).filter(Boolean);

  return card;
}

// The one exported entry point. Starts from a fresh initialFormState() and overwrites only what
// is derivable from `template`, so the result always carries the exact same top-level key set —
// never a hand-built object literal that could drift from state.js's own shape.
export function prefillFormState(template) {
  const source = (template && typeof template === 'object') ? template : {};
  const state = initialFormState();

  // ---- simple scalar fields ----
  state.templateName = field(source.name || '');
  state.language = field(source.language || '');
  // Real rows have been verified uppercase (e.g. category: 'UTILITY') but this normalises
  // defensively rather than assuming every row/environment agrees.
  state.category.value = source.category ? String(source.category).toUpperCase() : '';
  // status is already uppercase on the wire everywhere else in this app (helper/templateStatus.js
  // takes it as-is, no case transform) — pass it through unchanged.
  state.status = { value: source.status || '' };
  // A carousel row's top-level `type` is its media kind (IMAGE/VIDEO), not the wire TYPE this
  // form's own state.type represents. payload.js sends state.type.value unconditionally as
  // `type` on both create and update ("Required on both create and update"), so leaving this as
  // source.type here sends type: IMAGE/VIDEO with carouselCards attached, and Enterprise saves it
  // as a plain media template instead of a carousel (MessageTemplate.java:2996-3002). button_type
  // is checked ahead of carouselCards, same as the subcategory ladder below — some rows are
  // button_type CAROUSEL with no carouselCards key on the row at all (see that block's own
  // comment on qa_br_b7_1790158823 and friends).
  const isCarouselSource = source.button_type === 'CAROUSEL'
    || (Array.isArray(source.carouselCards) && source.carouselCards.length > 0);
  state.type = { value: isCarouselSource ? 'CAROUSEL' : (source.type || '') };

  // ---- subcategory ladder ----
  // Per-category: every result below MUST be one of that category's own Template Type option
  // values (sections/Category.js's SUBCATEGORIES_BY_CATEGORY) — Utility, for instance, has no
  // Carousel/LTO/Flow option at all, so those rungs only ever apply under Marketing. This is
  // deliberately narrower than the console's own ladder (gs-ui-templates
  // prefillCreateTemplateForm.ts), which also infers a product-message subcategory for Marketing
  // from the row's `type` — out of this task's specified scope, left as the plain "custom
  // message" default rather than guessed. Known gap, flagged for follow-up.
  const rawButtons = Array.isArray(source.buttons) ? source.buttons : [];
  const inferredAuth = state.category.value === CATEGORY.AUTHENTICATION
    ? inferAuthSubcategory(source, rawButtons)
    : null;
  // Enterprise's list API returns this as the STRING "true" (ce-api MessageTemplateService.java:
  // 2797-2799), not a real boolean — String()'d defensively rather than assuming every row/
  // environment agrees, same defensiveness as source.category's own uppercase normalisation above.
  const isCallPermissionRequestRow = String(source.call_permission_request).toLowerCase() === 'true';
  let selectedSubcategory = '';
  if (state.category.value === CATEGORY.MARKETING) {
    // button_type is a template-LEVEL wire field ('NONE'/'STATIC'/'COMPLEX'/'CAROUSEL'/...) —
    // distinct from a button's own `.type` (BUTTON_TYPE above), so it's not one of that enum's
    // members. Same raw-literal comparison DetailModal.js/helper/utils.js already use for it.
    // Checked ALONE, not just as a fallback when carouselCards is empty: three real rows
    // (qa_br_b7_1790158823, 2a2da4de_694d_4ea2_ab5b_d60ab73e9933, carousal) are button_type
    // CAROUSEL with NO carouselCards key on the row at all — Enterprise's list endpoint can
    // return a carousel template with its card data unpopulated. Falling through to the
    // carouselCards-only check would silently prefill these as a plain custom message, with no
    // signal anywhere that a carousel became a text template — see detectDroppedFeatures' own
    // matching warning for this exact gap. The ---- carousels ---- block below already leaves
    // state.carousels at its initialFormState() default (0 cards) when carouselCards is missing,
    // which is exactly the "nothing to preserve, start empty" state this case needs.
    if ((Array.isArray(source.carouselCards) && source.carouselCards.length > 0) || source.button_type === 'CAROUSEL') {
      selectedSubcategory = SUBCATEGORY.CAROUSEL;
    } else if (source.limitedTimeOffer) {
      selectedSubcategory = SUBCATEGORY.LTO;
    } else if (isCallPermissionRequestRow) {
      selectedSubcategory = SUBCATEGORY.CALL_PERMISSION_REQUEST;
    } else if (rawButtons.some((btn) => btn?.type === BUTTON_TYPE.FLOW)) {
      selectedSubcategory = SUBCATEGORY.FLOW;
    } else {
      selectedSubcategory = CUSTOM_MESSAGE_SUBCATEGORY;
    }
  } else if (state.category.value === CATEGORY.UTILITY) {
    if (rawButtons.some((btn) => btn?.type === BUTTON_TYPE.ORDER_DETAILS)) {
      selectedSubcategory = SUBCATEGORY.ORDER_DETAILS;
    } else if (isCallPermissionRequestRow) {
      selectedSubcategory = SUBCATEGORY.CALL_PERMISSION_REQUEST;
    } else {
      selectedSubcategory = CUSTOM_MESSAGE_SUBCATEGORY;
    }
  } else if (state.category.value === CATEGORY.AUTHENTICATION) {
    // null ("nothing derivable at all") falls back to COPY_CODE — the ladder rung needing the
    // least additional input — so the seeded OTP button below already satisfies validate.js's
    // exactly_one_otp_button_required with zero further user action. A real but unrecognised
    // otp_type ('' from inferAuthSubcategory) is intentionally left as '', not overwritten here.
    selectedSubcategory = inferredAuth === null ? SUBCATEGORY.COPY_CODE : inferredAuth.subcategory;
  }
  state.category.selectedSubcategory = field(selectedSubcategory);

  // ---- body + variables ----
  // Enterprise stores the body renumbered relative to the header (see templateVariables.js's
  // toFormBodyNumbering) — undo that shift here so this form's body field, and the variable rows
  // built from it, always start at {{1}} like every other field, and so re-submitting an
  // untouched body doesn't fail Enterprise's own "must start from {{1}}" validation. body_examples
  // is positional (by index, not by token label) and the shift only relabels tokens in place —
  // it never reorders them — so zipping examples against the shifted body's tokens in
  // buildVariableRows below still lines each sample value up with the right row.
  const formBody = toFormBodyNumbering(source.header, source.body || '');
  state.body.value = formBody;
  state.body.variable = buildVariableRows(formBody, source.body_examples);

  // ---- header ----
  // template.type itself (not a separate flag) distinguishes a media header — TemplateType's
  // IMAGE/VIDEO/DOCUMENT/GIF/LOCATION values double as the header media kind. LOCATION is
  // modelled the same way as the other four (state contract: header.value = MEDIA,
  // header.headerMedia.value = 'LOCATION') even though it carries no uploadable file — Enterprise
  // never returns a header_examples entry for it (no media sample exists to re-fetch a CDN URL
  // from), so mediaSrc simply stays '' the same way it would for a row with no header_examples at
  // all. validateForm's media-required check has its own LOCATION exemption so that blank mediaSrc
  // (and blank mediaId) never blocks submission.
  const mediaHeaderTypes = [MEDIA_TYPE.IMAGE, MEDIA_TYPE.VIDEO, MEDIA_TYPE.DOCUMENT, MEDIA_TYPE.GIF, MEDIA_TYPE.LOCATION];
  if (source.header) {
    state.header.value = HEADER_TYPE.TEXT;
    state.header.headerText.value = source.header;
    state.header.headerText.variable = buildVariableRows(source.header, source.header_examples);
  } else if (mediaHeaderTypes.includes(source.type)) {
    state.header.value = HEADER_TYPE.MEDIA;
    state.header.headerMedia.value = source.type;
    // source.header_examples[0] is the existing media's CDN URL (TemplateUtils.java:247-260) —
    // populated for preview only. Safe to set here (unlike the carousel-card case in
    // buildCarouselCardFromRaw above): validateForm's media check keys on headerMedia.mediaId
    // alone, so a populated mediaSrc with no real re-upload still correctly fails submission with
    // "existing media must be re-uploaded to save changes". mediaId/file stay blank — re-editing
    // this template's header media still requires the user to re-attach the file. For LOCATION
    // specifically, source.header_examples is never populated server-side, so this is always ''.
    state.header.headerMedia.mediaSrc = Array.isArray(source.header_examples) ? (source.header_examples[0] || '') : '';
  }
  // else: header.value stays HEADER_TYPE.NONE (initialFormState's own default) — no header.

  // ---- footer ----
  state.footer.footerText = field(source.footer || '');

  // ---- message validity ----
  if (source.message_send_ttl_seconds) {
    state.messageValidity.isValidityChecked = true;
    state.messageValidity.validityPeriod = field(String(source.message_send_ttl_seconds / 60));
  }

  // ---- limited time offer ----
  if (source.limitedTimeOffer) {
    state.lto.offerTitle = field(source.limitedTimeOffer.text || '');
    state.lto.isExpiryChecked = Boolean(source.limitedTimeOffer.has_expiration);
  }

  // ---- buttons ----
  let buttons;
  if (state.category.value === CATEGORY.AUTHENTICATION) {
    // Auth state carries only its own OTP/PASSKEY button — never a URL/CTA/quick-reply button
    // reconstructed from the row. In particular, an OTP-shaped URL button (otp_type= in its url)
    // is deliberately excluded here even though reconstructButton can build it just fine as an
    // ordinary URL button: its subcategory and text are already captured via inferredAuth/the
    // seed below, so keeping it too would make payload.js's bucketButtons ALSO send it as a
    // callToActionButtons entry on an AUTHENTICATION template.
    buttons = rawButtons
      .filter((btn) => btn?.type === BUTTON_TYPE.OTP || btn?.type === BUTTON_TYPE.PASSKEY)
      .map(reconstructButton)
      .filter(Boolean);
    if (buttons.length === 0) {
      // Never leave an Authentication template with buttons: [] — seed exactly one OTP/PASSKEY
      // button matching the inferred subcategory, so the form starts from a state
      // validate.js's exactly_one_otp_button_required check can already pass, even before the
      // user touches anything (same invariant AuthButtonsText.js establishes live for a user-driven
      // subcategory change; this just hands prefill the same starting point). Carries the
      // inferred source button's own text forward when inference actually came from a button
      // (e.g. an OTP-shaped URL button's text) — payload.js's buildOtpButton still sends it, and
      // AuthButtonsText.js's Copy Code / Autofill text fields still read it.
      const seeded = createButton(selectedSubcategory === SUBCATEGORY.PASSKEY ? BUTTON_TYPE.PASSKEY : BUTTON_TYPE.OTP);
      const sourceText = inferredAuth?.sourceButton?.text;
      if (sourceText) seeded.text = field(sourceText);
      buttons = [seeded];
    }
  } else {
    // .map().filter(Boolean) preserves rawButtons' own relative order (filtering out an
    // unsupported type never reorders what's left) — payload.js's buildPayload relies on that:
    // its quickReplyFirst decision reads whichever button sits at state.buttons[0], so a
    // quick-reply-led source row must still be quick-reply-led here, not just quick-reply-having.
    buttons = rawButtons.map(reconstructButton).filter(Boolean);
  }
  state.buttons = buttons;

  // ---- LTO buttons: mark the checkbox-managed rows locked ----
  // Category.js/Lto.js create these two live with locked: true so Buttons.js won't offer to
  // retype/remove/drag them (T6) — mark the equivalent rows here too, or a reloaded LTO template
  // would show them as ordinary, fully-editable buttons.
  if (selectedSubcategory === SUBCATEGORY.LTO) {
    const firstUrlButton = state.buttons.find((btn) => btn.type === BUTTON_TYPE.URL);
    if (firstUrlButton) firstUrlButton.locked = true;
    if (source.limitedTimeOffer?.has_expiration) {
      const copyCodeButton = state.buttons.find((btn) => btn.type === BUTTON_TYPE.COPY_CODE);
      if (copyCodeButton) copyCodeButton.locked = true;
    }
  }

  // ---- marketing opt-out detection ----
  // Enterprise overwrites the LAST quick reply's text and the footer whenever opt_out_button is
  // true (MessageTemplate.java:1575-1577, :1679-1682 — see state.js's MARKETING_OPT_OUT_BUTTON_TEXT
  // / MARKETING_OPT_OUT_FOOTER comment). Detecting that shape here is what makes an edit round-trip:
  // without it, state.buttons would keep "Stop Promotions" as an ordinary, editable quick reply and
  // payload.js's own opt-out append (gated on state.optOutButton) would never fire, so re-saving an
  // opt-out template unchanged would silently drop the opt-out on the next save. Heuristic, not a
  // real flag on the row (the list endpoint doesn't expose opt_out_button itself) — a genuinely
  // user-authored last quick reply that happens to read "Stop Promotions" on a Marketing template
  // is indistinguishable from a real opt-out button and is treated as one, same tradeoff as every
  // other inference in this file.
  if (state.category.value === CATEGORY.MARKETING) {
    const quickReplies = state.buttons.filter((btn) => btn.type === BUTTON_TYPE.QUICK_REPLY);
    const lastQuickReply = quickReplies[quickReplies.length - 1];
    const lastText = lastQuickReply ? lastQuickReply.text.value : '';
    if (lastQuickReply && lastText.trim().toLowerCase() === MARKETING_OPT_OUT_BUTTON_TEXT.trim().toLowerCase()) {
      state.optOutButton = true;
      // payload.js re-appends this exact button from the flag alone — keeping it in state.buttons
      // too would double it up on the next save (and let the user edit/delete the "real" text).
      state.buttons = state.buttons.filter((btn) => btn.buttonId !== lastQuickReply.buttonId);
      const footerValue = state.footer.footerText.value;
      if (footerValue.trim().toLowerCase() === MARKETING_OPT_OUT_FOOTER.trim().toLowerCase()) {
        state.footer.footerText = field('');
      }
    }
  }

  // ---- carousels ----
  if (Array.isArray(source.carouselCards) && source.carouselCards.length > 0) {
    const cards = source.carouselCards.map((cardObj) => buildCarouselCardFromRaw(cardObj));
    state.carousels = {
      // carousels.mediaType/buttonTypes are template-level (every card shares one media type on
      // the wire — payload.js sends a single carouselMediaType, not one per card), so both are
      // read off card 0 only, mirroring the console's own prefill (__cards[0]?.headerType /
      // __cards[0].buttons.map(...)). Falls back to the row's own top-level `type` (also
      // IMAGE/VIDEO for a carousel — see state.type just above) before the hardcoded IMAGE
      // default, for the case card 0's own header component carried no format.
      mediaType: cards[0]?.cardMedia?.value || source.type || CAROUSEL_MEDIA_TYPE.IMAGE,
      buttonTypes: cards[0]?.buttons?.map((btn) => btn.type) || [],
      cards,
    };
  }

  // ---- appsSetup / messageContent (from the OTP-shaped URL) ----
  // package_name/signature_hash/code_expiration_minutes/cta_display_name all live inside the
  // OTP-shaped URL button's own `url` when inferAuthSubcategory's rung 3 is what matched (see its
  // own comment — the only rung that fires against a real row today) — nothing to parse when
  // inference came from rung 1/2/4/5 instead (PASSKEY, a real OTP-typed button, sub_category, or
  // nothing derivable), none of which carry a `url` at all.
  const otpUrl = inferredAuth?.sourceButton?.url;
  if (typeof otpUrl === 'string') {
    const {
      packageName, signatureHash, codeExpirationMinutes, ctaDisplayName,
    } = parseOtpUrlParams(otpUrl);

    // Only Autofill/Zero-Tap actually show the App Setup card and send supported_apps
    // (AuthButtonsText.js's needsAutofillText / payload.js's buildOtpButton) — Copy Code has no app
    // identity to seed even when the URL happens to carry one.
    if ((selectedSubcategory === SUBCATEGORY.AUTOFILL || selectedSubcategory === SUBCATEGORY.ZERO_TAP)
      && (packageName || signatureHash)) {
      // Bug fix: package_name/signature_hash can each be a comma-separated list for several apps
      // (console mirror: prefillCreateTemplateForm.ts:184-199) — one row per index, via the same
      // factory AuthAppSetup.js's own mount effect uses, capped at its MAX_APP_SETUPS (5). This used
      // to build exactly one row out of the two raw (possibly comma-joined) strings, which failed
      // the package-name/signature-hash format check for any account with more than one app.
      const packageNames = packageName.split(',');
      const signatureHashes = signatureHash.split(',');
      state.appsSetup = packageNames.slice(0, 5).map((name, index) => {
        const appSetup = createAppSetup(createId('app-setup'));
        appSetup.packageName = field(name);
        appSetup.signatureHash = field(signatureHashes[index] || '');
        return appSetup;
      });
    }

    // Unlike appsSetup, not gated by subcategory — Copy Code OTP URLs carry
    // code_expiration_minutes too (task-verified), and messageContent.isExpirationTimeChecked/
    // expirationTime apply to every OTP-shaped subcategory alike (AuthMessageContent.js only hides this
    // whole card for Passkey, which never reaches this branch — a PASSKEY sourceButton has no url).
    if (codeExpirationMinutes) {
      state.messageContent.isExpirationTimeChecked = true;
      state.messageContent.expirationTime = field(codeExpirationMinutes);
    }

    // Bug fix: the autofill button's own display text was never carried back at all — console
    // mirror (prefillCreateTemplateForm.ts:180-199): __autofillText = getAutoFillData(url).
    // ctaDisplayName, read into the button AuthButtonsText.js's "Autofill button text" field writes.
    if ((selectedSubcategory === SUBCATEGORY.AUTOFILL || selectedSubcategory === SUBCATEGORY.ZERO_TAP)
      && ctaDisplayName && state.buttons[0]) {
      state.buttons[0].autofillText = field(ctaDisplayName);
    }
  }

  // ---- auth non-Passkey: fields Enterprise derives itself, never a raw row field ----
  // Passkey is excluded throughout this block — it's the one auth sub-type with a real,
  // user-editable body and footer (Body.js/Footer.js), and payload.js already guards
  // codeExpirationMinutes/addSecurityRecommendation to never send for it regardless.
  if (state.category.value === CATEGORY.AUTHENTICATION && selectedSubcategory !== SUBCATEGORY.PASSKEY) {
    const authButton = state.buttons[0];

    // Bug fix: this used to be an unconditional known gap (no raw list-row field has ever been
    // verified to carry addSecurityRecommendation itself) — detect it instead from the generated
    // body's own text, which Enterprise appends this exact sentence to when the flag was set
    // (MessageTemplate.java:1428-1429). English-only, matching Enterprise's own template catalogue
    // — see detectDroppedFeatures below for every other language.
    state.messageContent.isSecurityCheck = (source.body || '').includes('For your security, do not share this code.');

    // The console never renders an auth body either (Body/index.tsx hides it the same way ours
    // does); Enterprise regenerates it from scratch on every save and ignores whatever we send
    // (MessageTemplate.java:1506-1511) — reset to the same blank shape a fresh form starts with,
    // or Enterprise's own generated text would ride back to it as `template` (payload.js) and get
    // silently duplicated on the very next save.
    state.body = initialFormState().body;
    state.footer.footerText = field('');

    if (authButton && selectedSubcategory === SUBCATEGORY.ZERO_TAP) {
      // Enterprise hardcodes this true on its own reconciliation of a zero-tap button
      // (TemplateUtils.java:658) — a zero-tap button couldn't exist on a live template otherwise.
      // Console mirrors the same rule off otp_type alone (prefillCreateTemplateForm.ts's
      // __isZeroTapTermsChecked) — no wire field ever carries the real acceptance value back to us.
      authButton.zeroTapTermsAccepted = true;
    }

    // code_expiration_minutes normally arrives via the OTP-shaped URL's own query string (parsed
    // above) — when that didn't happen, Enterprise's stored footer text ("This code expires in 10
    // minute.", config.tpl:399) is the only other place the number survives.
    if (!state.messageContent.isExpirationTimeChecked) {
      const footerMinutesMatch = /(\d+)/.exec(source.footer || '');
      if (footerMinutesMatch) {
        state.messageContent.isExpirationTimeChecked = true;
        state.messageContent.expirationTime = field(footerMinutesMatch[1]);
      }
    }
  }

  return state;
}

// Investigates what prefillFormState above would drop or downgrade for a given raw row, without
// performing the actual prefill — Form/index.js calls this in clone mode only, to warn the user
// before they submit a clone that's silently thinner than the template they copied (editing
// already lives with these gaps; cloning must not hide them). Reuses prefillFormState's own
// recognizers (reconstructButton, inferAuthSubcategory) rather than re-deriving the drop
// conditions a second time, so the two can never drift apart. Returns an array of user-facing
// sentences, empty when nothing was dropped.
//
// Deliberately NOT covered here (see prefillFormState's own comments for why): header/body
// variable sample values and header/carousel-card media re-upload — both already surface through
// validate.js's own required-field errors at submit time, so they're visible, not silent.
// codeExpirationMinutes is no longer in this list either — it's parsed off the OTP-shaped URL and
// carried into state by prefillFormState's own parseOtpUrlParams call now, same source the
// app-credentials check below reads. addSecurityRecommendation stays uncovered — no field on a
// raw row has ever been verified to carry it, so there is nothing reliable to check.
export function detectDroppedFeatures(template) {
  const source = (template && typeof template === 'object') ? template : {};
  const dropped = [];

  // LOCATION header: prefillFormState's mediaHeaderTypes now recognises LOCATION the same as
  // IMAGE/VIDEO/DOCUMENT/GIF (state.header.value = MEDIA, headerMedia.value = 'LOCATION'), so it's
  // no longer dropped on clone — nothing to report here.

  const rawButtons = Array.isArray(source.buttons) ? source.buttons : [];
  const hasUnsupportedButton = rawButtons.some((btn) => btn && reconstructButton(btn) === null);
  if (hasUnsupportedButton) {
    dropped.push('One or more buttons on this template (e.g. a call-permission-request button) are not supported here and were left out.');
  }

  // Carousel cards funnel their buttons through the same reconstructButton, so the same
  // unsupported-type drop can happen per card, independent of the top-level check above.
  const rawCards = Array.isArray(source.carouselCards) ? source.carouselCards : [];
  const hasUnsupportedCarouselButton = rawCards.some((cardObj) => {
    const buttonsComponent = (Array.isArray(cardObj?.components) ? cardObj.components : [])
      .find((component) => component?.type === 'buttons');
    const cardButtons = Array.isArray(buttonsComponent?.buttons) ? buttonsComponent.buttons : [];
    return cardButtons.some((btn) => btn && reconstructButton(btn) === null);
  });
  if (hasUnsupportedCarouselButton) {
    dropped.push('One or more carousel card buttons on this template are not supported here and were left out.');
  }

  // Mirrors prefillFormState's own button_type==='CAROUSEL' check above -- a carousel-shaped row
  // with no carouselCards data prefills as a real (empty) Carousel now instead of silently
  // becoming a plain custom message, but "real, empty carousel" is still a template missing every
  // one of its cards. Surfaced here so the user sees why before rebuilding them, on both edit and
  // clone -- never silently let a carousel through as anything else.
  if (source.button_type === 'CAROUSEL' && !(Array.isArray(source.carouselCards) && source.carouselCards.length > 0)) {
    dropped.push("This template's carousel cards could not be loaded from WhatsApp and were left out — re-add them before saving.");
  }

  const category = source.category ? String(source.category).toUpperCase() : '';
  if (category === CATEGORY.AUTHENTICATION) {
    const authInference = inferAuthSubcategory(source, rawButtons);

    // inferAuthSubcategory returning null means prefillFormState above falls back to Copy Code
    // (the ladder rung needing the least input) rather than leaving the subcategory blank — a
    // real but unrecognised auth shape would otherwise silently become a different subcategory
    // than the one this template actually is.
    if (authInference === null) {
      dropped.push("This template's authentication type couldn't be determined, so it defaulted to Copy Code — check the Category section before submitting.");
    }

    // prefillFormState's isSecurityCheck detection matches the English security sentence
    // ("For your security, do not share this code.") in the generated body — it can't detect the
    // equivalent text in any other language, so a real security recommendation would silently fail
    // to re-tick on edit/clone for a non-English auth template. Passkey never shows this checkbox
    // at all (AuthMessageContent.js), so it's excluded the same way prefillFormState excludes it.
    const isPasskeyTemplate = (authInference === null ? SUBCATEGORY.COPY_CODE : authInference.subcategory) === SUBCATEGORY.PASSKEY;
    const isEnglish = ['en', 'en_US', 'en_GB'].includes(source.language);
    if (!isPasskeyTemplate && !isEnglish) {
      dropped.push("This template's security recommendation can't be detected in this language — re-tick it under Message Content if the original had it.");
    }

    // Autofill app credentials (package name / signature hash) live inside the OTP-shaped URL
    // button's own `url` (inferAuthSubcategory's rung 3) and are now carried into
    // state.appsSetup by prefillFormState's own parseOtpUrlParams call — so the only real gap
    // left to report is a ONE_TAP/ZERO_TAP row whose url genuinely never had them. Replaces a
    // dead check that used to test btn.type === BUTTON_TYPE.OTP, a wire shape that has never
    // once occurred on a real row (see inferAuthSubcategory's own comment on why rung 3, not
    // rung 2, is what actually fires) -- so it silently never ran.
    const needsAppCredentials = authInference?.subcategory === SUBCATEGORY.AUTOFILL
      || authInference?.subcategory === SUBCATEGORY.ZERO_TAP;
    if (needsAppCredentials && typeof authInference.sourceButton?.url === 'string') {
      const { packageName, signatureHash } = parseOtpUrlParams(authInference.sourceButton.url);
      if (!packageName || !signatureHash) {
        dropped.push("This template's autofill app credentials (package name / signature hash) aren't in WhatsApp's own data and couldn't be carried over — add them under App Setup before submitting.");
      }
    }

    // prefillFormState above keeps only the row's own OTP/PASSKEY button in state.buttons — any
    // other button is left out. A button recognised as this template's auth control (a real
    // OTP/PASSKEY button, or an OTP-shaped URL button whose otp_type/text were captured onto the
    // seeded button instead) is not a drop, so it's excluded here via reference equality against
    // authInference.sourceButton; anything else left over is a genuine drop.
    const consumedButton = authInference?.sourceButton;
    const hasExtraButton = rawButtons.some((btn) => (
      btn && btn.type !== BUTTON_TYPE.OTP && btn.type !== BUTTON_TYPE.PASSKEY && btn !== consumedButton
    ));
    if (hasExtraButton) {
      dropped.push("This template's additional buttons aren't supported on an authentication template here and were left out.");
    }
  }

  return dropped;
}
